import React, { useState } from 'react';
import { useCookieConsent } from '../context/CookieConsentContext';

const CookieConsentBanner = () => {
    const { showBanner, updatePreferences } = useCookieConsent();
    const [showDetails, setShowDetails] = useState(false);

    // Local state for the management view
    const [localPrefs, setLocalPrefs] = useState({
        analytics: true,
        functionality: true,
        payment: true,
        booking: true
    });

    // If banner should not be shown, return null
    if (!showBanner) {
        return null;
    }

    const handleAcceptAll = () => {
        updatePreferences({
            analytics: true,
            functionality: true,
            payment: true,
            booking: true
        });
    };

    const handleRejectNonEssential = () => {
        updatePreferences({
            analytics: false,
            functionality: false,
            payment: false,
            booking: false
        });
    };

    const handleSavePreferences = () => {
        updatePreferences(localPrefs);
    };

    const togglePref = (key) => {
        setLocalPrefs(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    return (
        <div style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: '#ffffff',
            boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.1)',
            zIndex: 9999,
            padding: '24px',
            borderTop: '1px solid #e5e7eb',
            maxHeight: '80vh',
            overflowY: 'auto'
        }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                {!showDetails ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div>
                            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.25rem', color: '#111827' }}>We value your privacy</h3>
                            <p style={{ margin: 0, color: '#4b5563', fontSize: '0.95rem', lineHeight: '1.5' }}>
                                We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic.
                                By clicking "Accept All Cookies", you consent to our use of cookies.
                                Read our <a href="/cookies" style={{ color: 'var(--lp-primary-orange)', textDecoration: 'underline' }}>Cookies Policy</a> for more information.
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'flex-end', marginTop: '8px' }}>
                            <button
                                onClick={() => setShowDetails(true)}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: 'transparent',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    color: '#374151',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Manage Preferences
                            </button>
                            <button
                                onClick={handleRejectNonEssential}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#f3f4f6',
                                    border: '1px solid transparent',
                                    borderRadius: '6px',
                                    color: '#374151',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Reject Non-Essential
                            </button>
                            <button
                                onClick={handleAcceptAll}
                                style={{
                                    padding: '8px 24px',
                                    backgroundColor: 'var(--lp-primary-orange, #f97316)',
                                    border: '1px solid transparent',
                                    borderRadius: '6px',
                                    color: 'white',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Accept All Cookies
                            </button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <h3 style={{ margin: '0 0 16px 0', fontSize: '1.25rem', color: '#111827' }}>Manage Cookie Preferences</h3>
                        <p style={{ marginBottom: '24px', color: '#4b5563', fontSize: '0.95rem' }}>
                            Select your cookie preferences below. Essential cookies are always enabled as they are necessary for the site to function.
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                            {/* Essential Cookies */}
                            <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <h4 style={{ margin: 0, fontWeight: '600' }}>Essential Cookies</h4>
                                    <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>Always Active</span>
                                </div>
                                <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                                    These cookies are required for basic site functionality and security. They cannot be disabled.
                                </p>
                            </div>

                            {/* Analytics Cookies */}
                            <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <h4 style={{ margin: 0, fontWeight: '600' }}>Analytics Cookies</h4>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={localPrefs.analytics}
                                            onChange={() => togglePref('analytics')}
                                            style={{ width: '18px', height: '18px', accentColor: 'var(--lp-primary-orange, #f97316)' }}
                                        />
                                    </label>
                                </div>
                                <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                                    Help us understand how visitors interact with our website. We use Google Analytics.
                                </p>
                            </div>

                            {/* Functionality Cookies */}
                            <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <h4 style={{ margin: 0, fontWeight: '600' }}>Functionality Cookies</h4>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={localPrefs.functionality}
                                            onChange={() => togglePref('functionality')}
                                            style={{ width: '18px', height: '18px', accentColor: 'var(--lp-primary-orange, #f97316)' }}
                                        />
                                    </label>
                                </div>
                                <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                                    Allow the website to remember choices you make and provide enhanced features.
                                </p>
                            </div>

                            {/* Payment Processing Cookies */}
                            <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <h4 style={{ margin: 0, fontWeight: '600' }}>Payment Processing Cookies</h4>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={localPrefs.payment}
                                            onChange={() => togglePref('payment')}
                                            style={{ width: '18px', height: '18px', accentColor: 'var(--lp-primary-orange, #f97316)' }}
                                        />
                                    </label>
                                </div>
                                <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                                    Used by our payment provider (Stripe) for secure transaction processing and fraud prevention. If disabled, you might not be able to process payments.
                                </p>
                            </div>

                            {/* Booking Cookies */}
                            <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <h4 style={{ margin: 0, fontWeight: '600' }}>Booking Cookies</h4>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={localPrefs.booking}
                                            onChange={() => togglePref('booking')}
                                            style={{ width: '18px', height: '18px', accentColor: 'var(--lp-primary-orange, #f97316)' }}
                                        />
                                    </label>
                                </div>
                                <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                                    Used by Microsoft Bookings to facilitate demo session scheduling.
                                </p>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
                            <button
                                onClick={() => setShowDetails(false)}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: 'transparent',
                                    color: '#6b7280',
                                    border: 'none',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                }}
                            >
                                Back
                            </button>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <button
                                    onClick={handleAcceptAll}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#f3f4f6',
                                        border: '1px solid transparent',
                                        borderRadius: '6px',
                                        color: '#374151',
                                        fontWeight: '500',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Accept All
                                </button>
                                <button
                                    onClick={handleSavePreferences}
                                    style={{
                                        padding: '8px 24px',
                                        backgroundColor: 'var(--lp-primary-orange, #f97316)',
                                        border: '1px solid transparent',
                                        borderRadius: '6px',
                                        color: 'white',
                                        fontWeight: '500',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Save Preferences
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CookieConsentBanner;
